/**
 * Service for managing golf course data
 */
import { v4 as uuidv4 } from 'uuid';
import { GolfCourse } from '../types';

/**
 * Mock data for golf courses
 */
export const mockGolfCourses: GolfCourse[] = [
  {
    id: '7',
    name: 'The Crossvines',
    location: 'Poolesville, MD',
    description: 'A premier golf destination stretching over 380 picturesque acres in the lush Potomac Valley. With almost 7,000 yards of championship golf and world-class practice facilities, The Crossvines offers a beautiful course that challenges golfers of all levels.',
    imageUrl: 'https://images.pexels.com/photos/7242696/pexels-photo-7242696.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    holeCount: 18,
    difficulty: 'Intermediate',
    amenities: ['Pro Shop', 'Bistro', 'Driving Range', 'Short Game Practice Area', 'Wine Venue', 'Golf Lessons', 'Corporate Events'],
    pricingRange: {
      weekday: {
        min: 85,
        max: 120
      },
      weekend: {
        min: 130,
        max: 180
      }
    },
    rating: 4.7,
    contactInfo: {
      phone: '(301) 428-8143',
      email: 'info@crossvines.com',
      website: 'www.crossvines.com'
    },
    latitude: 39.1402,
    longitude: -77.4108
  },
  {
    id: '1',
    name: 'Pine Valley Golf Club',
    location: 'Pine Valley, NJ',
    description: 'Consistently ranked as one of the top golf courses in the United States, Pine Valley offers a challenging yet rewarding experience for golfers of all skill levels.',
    imageUrl: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    holeCount: 18,
    difficulty: 'Advanced',
    amenities: ['Pro Shop', 'Restaurant', 'Driving Range', 'Putting Green', 'Locker Rooms', 'Golf Lessons'],
    pricingRange: {
      weekday: {
        min: 120,
        max: 180
      },
      weekend: {
        min: 180,
        max: 250
      }
    },
    rating: 4.9,
    contactInfo: {
      phone: '(555) 123-4567',
      email: 'info@pinevalley.com',
      website: 'www.pinevalley.com'
    },
    latitude: 39.7943,
    longitude: -74.9701
  },
  {
    id: '2',
    name: 'Augusta National',
    location: 'Augusta, GA',
    description: 'Home of the Masters Tournament, Augusta National is a private club with an iconic course layout featuring azaleas, towering pines, and pristine fairways.',
    imageUrl: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    holeCount: 18,
    difficulty: 'Pro',
    amenities: ['Pro Shop', 'Fine Dining', 'Caddie Service', 'Practice Facilities', 'Clubhouse'],
    pricingRange: {
      weekday: {
        min: 200,
        max: 300
      },
      weekend: {
        min: 300,
        max: 400
      }
    },
    rating: 5.0,
    contactInfo: {
      phone: '(555) 765-4321',
      email: 'members@augustanational.com',
      website: 'www.augustanational.com'
    },
    latitude: 33.5022,
    longitude: -82.0251
  },
  {
    id: '3',
    name: 'Pebble Beach Golf Links',
    location: 'Pebble Beach, CA',
    description: 'Perched on the rugged coastline of the Monterey Peninsula, Pebble Beach offers breathtaking ocean views and a legendary golfing experience.',
    imageUrl: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    holeCount: 18,
    difficulty: 'Advanced',
    amenities: ['Ocean View Dining', 'Spa', 'Pro Shop', 'Golf Academy', 'Tennis Courts', 'Luxury Accommodations'],
    pricingRange: {
      weekday: {
        min: 250,
        max: 350
      },
      weekend: {
        min: 350,
        max: 550
      }
    },
    rating: 4.8,
    contactInfo: {
      phone: '(555) 987-6543',
      email: 'reservations@pebblebeach.com',
      website: 'www.pebblebeach.com'
    },
    latitude: 36.5725,
    longitude: -121.9486
  },
  {
    id: '4',
    name: 'St. Andrews Links',
    location: 'St. Andrews, Scotland',
    description: 'Known as the "Home of Golf," St. Andrews Old Course is the oldest golf course in the world and a bucket-list destination for golf enthusiasts.',
    imageUrl: 'https://images.pexels.com/photos/914682/pexels-photo-914682.jpeg?auto=compress&cs=tinysrgb&w=1350',
    holeCount: 18,
    difficulty: 'Advanced',
    amenities: ['Historic Clubhouse', 'Golf Museum', 'Traditional Caddie Service', 'Restaurant', 'Pro Shop'],
    pricingRange: {
      weekday: {
        min: 180,
        max: 250
      },
      weekend: {
        min: 250,
        max: 350
      }
    },
    rating: 4.9,
    contactInfo: {
      phone: '+44 1334 466666',
      email: 'info@standrews.com',
      website: 'www.standrews.com'
    },
    latitude: 56.3416,
    longitude: -2.8038
  },
  {
    id: '5',
    name: 'Clearwater Golf Club',
    location: 'Tampa, FL',
    description: 'A family-friendly golf club with palm-lined fairways, sparkling water features, and a welcoming atmosphere for golfers of all ages.',
    imageUrl: 'https://images.pexels.com/photos/2081319/pexels-photo-2081319.jpeg?auto=compress&cs=tinysrgb&w=1350',
    holeCount: 18,
    difficulty: 'Intermediate',
    amenities: ['Family Clubhouse', 'Lessons for Kids', 'Pool', 'Casual Dining', 'Pro Shop', 'Practice Areas'],
    pricingRange: {
      weekday: {
        min: 60,
        max: 90
      },
      weekend: {
        min: 90,
        max: 120
      }
    },
    rating: 4.3,
    contactInfo: {
      phone: '(555) 333-2222',
      email: 'play@clearwatergolf.com',
      website: 'www.clearwatergolf.com'
    },
    latitude: 27.9659,
    longitude: -82.8001
  },
  {
    id: '6',
    name: 'Mountain View Golf Resort',
    location: 'Denver, CO',
    description: 'Set against the backdrop of the majestic Rocky Mountains, this course combines challenging play with stunning natural beauty.',
    imageUrl: 'https://images.pexels.com/photos/1473050/pexels-photo-1473050.jpeg?auto=compress&cs=tinysrgb&w=1350',
    holeCount: 18,
    difficulty: 'Intermediate',
    amenities: ['Mountain Lodge', 'Ski & Golf Packages', 'Spa', 'Fine Dining', 'Heated Practice Areas'],
    pricingRange: {
      weekday: {
        min: 100,
        max: 150
      },
      weekend: {
        min: 150,
        max: 200
      }
    },
    rating: 4.5,
    contactInfo: {
      phone: '(555) 444-7777',
      email: 'info@mountainviewgolf.com',
      website: 'www.mountainviewgolf.com'
    },
    latitude: 39.7392,
    longitude: -104.9903
  }
];

/**
 * Gets all available golf courses
 * @returns Array of golf courses
 */
export const getAllGolfCourses = (): GolfCourse[] => {
  return mockGolfCourses;
};

/**
 * Gets a specific golf course by ID
 * @param courseId The ID of the golf course
 * @returns The golf course or undefined if not found
 */
export const getGolfCourseById = (courseId: string): GolfCourse | undefined => {
  return mockGolfCourses.find(course => course.id === courseId);
};

/**
 * Gets popular golf courses (highest rated)
 * @param limit The maximum number of courses to return
 * @returns Array of top-rated golf courses
 */
export const getPopularGolfCourses = (limit: number = 3): GolfCourse[] => {
  return [...mockGolfCourses]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, limit);
};

/**
 * Gets golf courses filtered by various criteria
 * @param filters Object containing filter criteria
 * @returns Array of filtered golf courses
 */
export const getFilteredGolfCourses = (filters: {
  difficulty?: GolfCourse['difficulty'][];
  priceRange?: { min: number; max: number };
  holeCount?: number;
  amenities?: string[];
}): GolfCourse[] => {
  return mockGolfCourses.filter(course => {
    // Filter by difficulty
    if (filters.difficulty && filters.difficulty.length > 0) {
      if (!filters.difficulty.includes(course.difficulty)) {
        return false;
      }
    }
    
    // Filter by price range (using weekday min as reference point)
    if (filters.priceRange) {
      if (
        course.pricingRange.weekday.min < filters.priceRange.min ||
        course.pricingRange.weekday.min > filters.priceRange.max
      ) {
        return false;
      }
    }
    
    // Filter by hole count
    if (filters.holeCount && course.holeCount !== filters.holeCount) {
      return false;
    }
    
    // Filter by amenities
    if (filters.amenities && filters.amenities.length > 0) {
      // Check if the course has all the requested amenities
      return filters.amenities.every(amenity => 
        course.amenities.includes(amenity)
      );
    }
    
    return true;
  });
};
